import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useClearRefinements, useRefinementList } from 'react-instantsearch';
import { keyToString, normalizeText } from '../../../util/data';
import { FormattedMessage } from '../../../util/reactIntl';
import css from './Filters.module.css';
import IconCollection from '../../../components/IconCollection/IconCollection';
import { Button } from '../../../components';
import { createResourceLocatorString } from '../../../util/routes';
import { omit, debounce, get } from 'lodash';
import { useSelector } from 'react-redux';
import { currentUserIdSelector } from '../../../ducks/user.duck';
import { sendFacetClickedEvent } from '../../../analytics/AlgoliaEvents';
import { LANGUAGES_FLUENCY_FILTER, PRIMARY_DESTINATIONS_FILTER, PRIMARY_SERVICES_FILTER, RATING_FILTER, SECONDARY_DESTINATIONS_FILTER } from '../../../constants';
import { TRAVEL_TYPES } from '../../../util/types';

const removeItemFromString = (str, itemToRemove) => {
  if (!str) return '';
  return str
    .split(',')
    .map(item => item.trim())
    .filter(item => item !== itemToRemove)
    .join(', ');
};

const onlyShowFluency = str => (str.includes('_') ? str.split('_')[1] : str);

const transformItems = items => items.sort((a, b) => a.value.localeCompare(b.value));

const LanguageFluency = React.memo(({
  currentUserId,
  attribute,
  queryParams,
  routes,
  history,
  parentItem,
}) => {
  const { items, refine } = useRefinementList({ attribute, transformItems });

  const fluencyItems = items.filter(item => item.label.includes(parentItem.label));
  const selectedItems = queryParams?.[attribute]?.length
    ? queryParams?.[attribute]?.split(',')
    : fluencyItems.map(st => st.label);

  const handleFluencyChange = useCallback((item, currentValues) => {
    const isRefined = selectedItems?.includes(item.value);
    let value =
      isRefined && !currentValues && selectedItems.length
        ? selectedItems.filter(selectedItem => selectedItem !== item.value)?.join(',') // initially queryParams do not contain the secondary destinations, so manually adding the ones not clicked
        : isRefined && currentValues
        ? removeItemFromString(currentValues, item.value)
        : currentValues
        ? `${currentValues},${item.value}`
        : item.value;

    if (!isRefined) {
      sendFacetClickedEvent({ currentUserId, attribute, value: item.value });
      refine(item.value);
    }

    const updateQueryParams = omit(queryParams, [attribute]);

    // If last secondary destination is being removed them also remove the primary destination
    if (
      attribute === SECONDARY_DESTINATIONS_FILTER &&
      selectedItems.length === 1 &&
      selectedItems?.[0] === item.value
    ) {
      delete updateQueryParams?.[PRIMARY_DESTINATIONS_FILTER];
    }

    // If fluency params are added to the url query params then it will also filter based on the parent language which will not be accurate.
    history.push(
      createResourceLocatorString(
        'SearchPage',
        routes,
        {},
        {
          ...(value ? { ...updateQueryParams, [attribute]: value } : updateQueryParams),
        }
      )
    );
  }, [currentUserId, attribute, queryParams, history, routes, refine, items.length]);

  return (
    <ul>
      {fluencyItems.map(item => (
        <li key={item.label}>
          <label>
            <input
              type="checkbox"
              checked={selectedItems?.includes(item.value)}
              onChange={() => handleFluencyChange(item, queryParams[attribute])}
              className={css.checkbox}
            />
            <span
              className={`${css.label} ${selectedItems?.includes(item.value) ? css.activeLabel : ''
                }`}
            >
              {normalizeText(onlyShowFluency(item.label))}
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
});

const CustomRefinementList = ({ attribute, label, history, routes, queryParams }) => {
  const [isOpened, setIsOpened] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const currentUserId = useSelector(currentUserIdSelector);
  const debouncedHistoryPushRef = useRef();

  const { items, refine } = useRefinementList({ attribute, transformItems, limit: 100 });
  const { canRefine: canClear, refine: clearRefine } = useClearRefinements({
    includedAttributes: [attribute],
  });

  useEffect(() => {
    debouncedHistoryPushRef.current = debounce(updatedQueryParams => {
      history.push(createResourceLocatorString('SearchPage', routes, {}, updatedQueryParams));
    }, 300);

    return () => {
      debouncedHistoryPushRef.current?.cancel();
    };
  }, [history, routes]);

  const handleRefinementChange = useCallback(
    (item, currentValue) => {
      const isRefined = queryParams[attribute]?.includes(item.value) || item.isRefined;
      // Optimize value calculation
      const value = isRefined && currentValue
        ? removeItemFromString(currentValue, item.value)
        : currentValue
          ? `${currentValue},${item.value}`
          : item.value;

      // Only send analytics event when necessary
      if (!isRefined) {
        sendFacetClickedEvent({ currentUserId, attribute, value: item.value });
      }

      // Create query params only once
      const updatedQueryParams = {
        ...omit(queryParams, [attribute]),
        ...(value && { [attribute]: value }),
      };

      // If primary destination filter is being removed then remove the secondary destination too 
      if (
        attribute === PRIMARY_DESTINATIONS_FILTER &&
        !updatedQueryParams[PRIMARY_DESTINATIONS_FILTER]
      ) {
        delete updatedQueryParams?.[SECONDARY_DESTINATIONS_FILTER];
      }

      debouncedHistoryPushRef.current(updatedQueryParams);
    },
    [attribute, currentUserId, queryParams, refine]
  );

  const handleClear = useCallback(() => {
    const updatedQueryParams = omit(queryParams, [attribute]);
    debouncedHistoryPushRef.current(updatedQueryParams);
    clearRefine();
  }, [attribute, queryParams, clearRefine]);

  const filteredItems = attribute === RATING_FILTER ? items.filter(item => item.label !== "0") : items;

  const displayedItems = attribute === PRIMARY_SERVICES_FILTER ? filteredItems.filter((st)=>  TRAVEL_TYPES.findIndex((it)=> it.label==st.label)>=0) :  showAll ? filteredItems : filteredItems.slice(0, 10);
  function getStars(number) {
    return number === 1 ? "1 Star" : `${number} Stars`;
  }

  const isSelected = item => {
    const secondaryFilter =
      attribute === PRIMARY_DESTINATIONS_FILTER && queryParams?.[SECONDARY_DESTINATIONS_FILTER];
    return secondaryFilter && typeof secondaryFilter === 'string'
      ? secondaryFilter?.includes(item.value)
      : item?.isRefined;
  };

  if (!items.length) return null;

  return (
    <div className={css.refinementContainer}>
      <div className={css.refinementListControllers}>
        <span className={css.genreTitle}>{label}</span>
        <Button
          className={isOpened ? css.minusBtn : css.plusBtn}
          onClick={() => setIsOpened(!isOpened)}
        >
          <IconCollection icon={isOpened ? 'minus-icon' : 'plus-transparent'} />
        </Button>
      </div>
      <div className={isOpened ? css.active : css.inActive}>
        <ul className={css.refinementList}>
          {displayedItems.map((item, index) => (
            <li key={item.label} className={css.refinementItem}>
              <label>
                <input
                  type="checkbox"
                  checked={isSelected(item)}
                  onChange={() => handleRefinementChange(item, queryParams[attribute])}
                  className={css.checkbox}
                />
                <span className={`${css.label} ${isSelected(item) ? css.activeLabel : ''}`}>
                  {/* {keyToString(item.label)} */}
                  {attribute === RATING_FILTER ? getStars(item.label) : item.label}
                </span>
              </label>
              {isSelected(item) ? (
                <LanguageFluency
                  currentUserId={currentUserId}
                  attribute={
                    attribute == PRIMARY_DESTINATIONS_FILTER
                      ? SECONDARY_DESTINATIONS_FILTER
                      : LANGUAGES_FLUENCY_FILTER
                  }
                  queryParams={queryParams}
                  routes={routes}
                  history={history}
                  parentItem={item}
                />
              ) : null}
            </li>
          ))}
        </ul>
        {attribute == PRIMARY_SERVICES_FILTER
          ? null
          : items.length > 10 && (
              <div className={css.showMoreButton} onClick={() => setShowAll(!showAll)}>
                {showAll ? (
                  <FormattedMessage id="RefinementList.showLess" />
                ) : (
                  <FormattedMessage id="RefinementList.showMore" />
                )}
              </div>
            )}
        <Button className={css.clearButton} onClick={handleClear}>
          <FormattedMessage id="RefinementList.clear" />
        </Button>
      </div>
    </div>
  );
};

export default React.memo(CustomRefinementList);
